import React, { useState } from 'react';
import { Check, Save, Eye } from 'lucide-react';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';

interface ReflexionData {
  // Klienten-Feedback
  kf_zufriedenheit: number;
  kf_gut_funktioniert: string;
  kf_besser_sein: string;
  kf_fortschritt_ziele: number;
  kf_beziehung_coach: number;
  kf_weiterempfehlung: number;
  kf_anmerkungen: string;
  // Coach-Reflexion
  cr_gut_gelaufen: string;
  cr_anders_machen: string;
  cr_muster: string;
  cr_methodenmix: string;
  cr_supervision: string;
  cr_naechste_schritte: string;
}

const defaultData: ReflexionData = {
  kf_zufriedenheit: 5,
  kf_gut_funktioniert: '',
  kf_besser_sein: '',
  kf_fortschritt_ziele: 5,
  kf_beziehung_coach: 5,
  kf_weiterempfehlung: 5,
  kf_anmerkungen: '',
  cr_gut_gelaufen: '',
  cr_anders_machen: '',
  cr_muster: '',
  cr_methodenmix: '',
  cr_supervision: '',
  cr_naechste_schritte: '',
};

interface Props {
  data: any;
  onSave: (data: any) => Promise<void>;
  blockId: string;
  clientEmail: string;
}

export function ReflexionForm({ data, onSave, blockId, clientEmail }: Props) {
  const { showToast } = useToast();
  const [form, setForm] = useState<ReflexionData>({ ...defaultData, ...data });
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'feedback' | 'reflexion'>('feedback');

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch { /* ignore */ }
    setSaving(false);
  };

  const handleExport = async () => {
    try {
      const res = await api.get(`/blocks/${blockId}/export/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Reflexion.pdf';
      a.click();
      URL.revokeObjectURL(url);
      showToast('PDF heruntergeladen', 'success');
    } catch { showToast('Export fehlgeschlagen', 'error'); }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'range') {
      setForm({ ...form, [name]: Number(value) });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const renderScale = (name: keyof ReflexionData, label: string) => (
    <div className="form-group">
      <label>{label}</label>
      <div className="range-display">
        <input type="range" name={name} min={1} max={10} value={form[name] as number} onChange={handleChange} style={{ flex: 1 }} />
        <span className="range-value">{form[name]}</span>
      </div>
    </div>
  );

  return (
    <div className="block-form">
      <div className="reflexion-tabs">
        <button
          className={`reflexion-tab ${activeTab === 'feedback' ? 'active' : ''}`}
          onClick={() => setActiveTab('feedback')}
        >
          Klienten-Feedback
        </button>
        <button
          className={`reflexion-tab ${activeTab === 'reflexion' ? 'active' : ''}`}
          onClick={() => setActiveTab('reflexion')}
        >
          Coach-Reflexion
        </button>
      </div>

      {activeTab === 'feedback' && (
        <div>
          <div className="block-form-section">
            <h4>Klienten-Feedback</h4>
            {renderScale('kf_zufriedenheit', 'Zufriedenheit mit dem Coaching-Prozess')}
            <div className="form-group">
              <label>Was hat gut funktioniert?</label>
              <textarea name="kf_gut_funktioniert" value={form.kf_gut_funktioniert} onChange={handleChange} placeholder="Positive Aspekte des Coachings..." />
            </div>
            <div className="form-group">
              <label>Was könnte besser sein?</label>
              <textarea name="kf_besser_sein" value={form.kf_besser_sein} onChange={handleChange} placeholder="Verbesserungsvorschläge..." />
            </div>
            {renderScale('kf_fortschritt_ziele', 'Fortschritt bei den Zielen')}
            {renderScale('kf_beziehung_coach', 'Beziehung zum Coach')}
            {renderScale('kf_weiterempfehlung', 'Weiterempfehlungs-Wahrscheinlichkeit')}
            <div className="form-group">
              <label>Offene Anmerkungen</label>
              <textarea name="kf_anmerkungen" value={form.kf_anmerkungen} onChange={handleChange} placeholder="Sonstige Anmerkungen..." />
            </div>
          </div>
        </div>
      )}

      {activeTab === 'reflexion' && (
        <div>
          <div className="block-form-section">
            <h4>Coach-Reflexion</h4>
            <div className="form-group">
              <label>Was ist gut gelaufen?</label>
              <textarea name="cr_gut_gelaufen" value={form.cr_gut_gelaufen} onChange={handleChange} placeholder="Erfolgreiche Aspekte..." />
            </div>
            <div className="form-group">
              <label>Was hätte ich anders machen können?</label>
              <textarea name="cr_anders_machen" value={form.cr_anders_machen} onChange={handleChange} placeholder="Optimierungsmöglichkeiten..." />
            </div>
            <div className="form-group">
              <label>Welche Muster erkenne ich beim Klienten?</label>
              <textarea name="cr_muster" value={form.cr_muster} onChange={handleChange} placeholder="Wiederkehrende Muster, Verhaltensweisen..." />
            </div>
            <div className="form-group">
              <label>Passt mein Methodenmix?</label>
              <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
                {['Ja', 'Nein', 'Anpassung nötig'].map((opt) => (
                  <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.88rem' }}>
                    <input
                      type="radio"
                      name="cr_methodenmix"
                      value={opt.toLowerCase().replace(/ /g, '_')}
                      checked={form.cr_methodenmix === opt.toLowerCase().replace(/ /g, '_')}
                      onChange={handleChange}
                    />
                    {opt}
                  </label>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label>Brauche ich Supervision/Intervision?</label>
              <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
                {['Ja', 'Nein'].map((opt) => (
                  <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.88rem' }}>
                    <input
                      type="radio"
                      name="cr_supervision"
                      value={opt.toLowerCase()}
                      checked={form.cr_supervision === opt.toLowerCase()}
                      onChange={handleChange}
                    />
                    {opt}
                  </label>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label>Nächste Schritte</label>
              <textarea name="cr_naechste_schritte" value={form.cr_naechste_schritte} onChange={handleChange} placeholder="Geplante nächste Schritte..." />
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border-light)' }}>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          <Save size={16} /> {saving ? 'Speichern...' : 'Speichern'}
        </button>
        <button className="btn btn-secondary" onClick={handleExport}>
          <Eye size={16} /> PDF-Vorschau
        </button>
        {saved && (
          <span className="save-indicator">
            <Check size={14} /> Gespeichert
          </span>
        )}
      </div>
    </div>
  );
}
